import React, { useState, useEffect } from 'react';
import { useCart } from '../../../services/CartService';
import { ProductModel } from 'models/ProductModel';
import { SquareMinus, SquarePlus } from 'lucide-react';
import { Button } from 'components/ui/button';

interface SimpleProductCardProps extends ProductModel {
  onClickProduct: (productId: string) => void;
}

const SimpleProductCard: React.FC<SimpleProductCardProps> = ({ image, price, minimum, productName, productId, unitFormat, stock, onClickProduct }) => {
  const [quantity, setQuantity] = useState(0);
  const { addToCart, removeFromCart, getCart } = useCart();

  useEffect(() => {
    const cart = getCart();
    const product = cart.find(item => item.productId === productId);
    if (product) {
      setQuantity(product.quantity);
    } else {
      setQuantity(0);
    }
  }, [productId, getCart, quantity]);

  const handleAddToCart = (e: React.MouseEvent<HTMLButtonElement | SVGElement>) => {
    e.stopPropagation();
    addToCart(productId, 1, true);
    const updatedCart = getCart();
    const product = updatedCart.find(item => item.productId === productId);
    if (product) setQuantity(product.quantity);
  };

  const handleRemoveFromCart = (e: React.MouseEvent<HTMLButtonElement | SVGElement>) => {
    e.stopPropagation();
    removeFromCart(productId, 1);
    const updatedCart = getCart();
    const product = updatedCart.find(item => item.productId === productId);
    if (product) {
      setQuantity(product.quantity);
    } else {
      setQuantity(0);
    }
  };

  function formatUnit(minimum:number, unitFormat?:string) {
    // Vérifie si on doit mettre au pluriel (pas pour les abréviations ≤ 3 caractères)
    if (unitFormat && minimum > 1 && unitFormat.length > 3) {
      // Si l'unité finit par "eau" (ex: "morceau"), on ajoute "x", sinon "s"
      if (unitFormat.endsWith("au")) {
        return `${minimum.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${unitFormat}x`;
      } else {
        return `${minimum.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${unitFormat}s`;
      }
    }
  
    return `${minimum.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}${unitFormat}`;
  }

  return (
    <div 
      className="flex flex-col gap-2 bg-white rounded-md shadow-sm cursor-move hover:shadow-md [&_.appear]:hover:bottom-2" 
      data-testid="productCard"
    >
      <div
        className="bg-[#A0A0A0] relative rounded-t-md h-36 bg-cover bg-no-repeat bg-center overflow-hidden flex justify-center items-end" 
        style={{backgroundImage: `url(${image})`}}
        >
        <Button
          variant="contain-white" 
          size="sm"
          className='appear relative bottom-2 md:-bottom-10 transition-all delay-75'
          onPointerDown={(event) => event.stopPropagation()}
          onClick={() => {
            onClickProduct(productId)
          }}
        >
          Plus d'information
          </Button>
      </div>
      <div className='flex justify-between items-center px-2'>
        <div>
          <p
            className="text-md font-bold text-black"
            data-testid="productTotalPrice">
            {(price * minimum).toLocaleString("fr-FR", { minimumFractionDigits: 2 })}€
          </p>
          <p
            className="text-xs text-[#A0A0A0]"
            data-testid="productUnitPrice">
            {price.toLocaleString("fr-FR", { minimumFractionDigits: 2 })}€/{unitFormat}
          </p>
        </div>
        <div>
          {quantity > 0 && stock > 0 ? (
            <div className="flex items-center ml-1" data-testid="quantityControl">
              <SquareMinus
                strokeWidth={1}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(e) => handleRemoveFromCart(e)}
                size={25}
                className='bg-white text-black cursor-pointer hover:bg-gray-100'
                data-testid="decreaseQuantityButton"
              />
              <span className="text-md font-bold mx-1" data-testid="quantityDisplay">{quantity}</span>
              <SquarePlus
                strokeWidth={1}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(e) => handleAddToCart(e)}
                size={32}
                className='bg-white text-black cursor-pointer hover:bg-gray-100'
                data-testid="increaseQuantityButton"
              />
            </div>
          ) : (
            stock > 0 ? 
              <SquarePlus
                strokeWidth={1}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(e) => handleAddToCart(e)}
                size={32}
                className='bg-white text-black cursor-pointer hover:bg-gray-100'
                data-testid="increaseQuantityButton"
              />
            : <span>Indisponible</span>
            // <Button
            //   onPointerDown={(event) => event.stopPropagation()}
            //   onClick={(e) => handleAddToCart(e)}
            //   size={"sm"}
            //   variant="outline-muted"
            //   className='border-black text-black'
            //   data-testid="addToCartButton"
            //   disabled={stock > 0 ? false : true}
            // >
            // </Button>
          )}
        </div>
      </div>
      <div className='px-2 mb-1 pb-2'>
        <hr/>
        <h4 className="text-sm mt-2 text-black mb-2" data-testid="productName">
          {productName.charAt(0).toUpperCase() + productName.slice(1).toLowerCase()}
        </h4>
        <p className='text-xs font-bold text-[#A0A0A0]'>Colis : {formatUnit(minimum, unitFormat)}</p>
      </div>
    </div>
  );
};

export default SimpleProductCard;